class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onHeaderClick = this.onHeaderClick.bind(this);
    this.onHeaderKeydown = this.onHeaderKeydown.bind(this);
    this.updateHeaderAccessibility = this.updateHeaderAccessibility.bind(this);
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !this.classList.contains('active')) return;
      const inner = this.querySelector('.drawer__inner');
      const focusable = getFocusableElements(inner).filter((element) => element.getClientRects().length && !element.closest('[inert], [hidden]'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        event.stopPropagation();
        (event.shiftKey ? last : first)?.focus();
      }
    });
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
      if (event.target.closest('[data-cart-retry]')) this.recoverCart();
      const offer = event.target.closest('[data-cart-discount-apply]');
      if (offer) this.changeDiscount(offer.dataset.cartDiscountApply, { featured: true });
      if (event.target.closest('[data-cart-discount-toggle]') && !this.isBusy) {
        this.setDiscountExpanded(!this.discountDetailsOpen, true);
      }
      const remove = event.target.closest('[data-cart-discount-remove]');
      if (remove) this.changeDiscount(remove.dataset.cartDiscountRemove, { remove: true });
      const protection = event.target.closest('[data-cart-protection-toggle]');
      // Expand the switch's tap area without capturing the coverage link or
      // forwarding a second click from the native label/checkbox.
      if (protection && !event.target.closest('a, input, label, button, select, textarea')) {
        const input = protection.querySelector('[data-cart-protection-input]');
        if (input && !input.disabled) input.click();
      }
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-cart-protection-input]')) this.setProtection(event.target.checked);
    });
    this.addEventListener('submit', (event) => {
      if (event.target.matches('[data-cart-discount-form]')) {
        event.preventDefault();
        event.stopPropagation();
        this.changeDiscount(event.target.querySelector('input[name="discount"]').value);
        return;
      }
      if (this.isBusy || this.cartNeedsRefresh || this.readState()?.merchandise_count === 0) event.preventDefault();
    });
    this.addEventListener('input', (event) => {
      if (event.target.id === 'CartDrawer-DiscountCode') this.discountDraft = event.target.value;
    });
  }

  connectedCallback() {
    document.addEventListener('click', this.onHeaderClick);
    document.addEventListener('keydown', this.onHeaderKeydown);
    document.addEventListener('shopify:section:load', this.updateHeaderAccessibility);
    this.updateHeaderAccessibility();
    this.normalizeInitialCart();
    this.refreshCurrency();
    this.updateDiscountUI();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onHeaderClick);
    document.removeEventListener('keydown', this.onHeaderKeydown);
    document.removeEventListener('shopify:section:load', this.updateHeaderAccessibility);
    if (this.classList.contains('active')) this.close();
  }

  onHeaderClick(event) {
    const link = event.target.closest('#cart-icon-bubble');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.open(link);
  }

  onHeaderKeydown(event) {
    if (event.code !== 'Space' || !event.target.closest('#cart-icon-bubble')) return;
    event.preventDefault();
    this.open(event.target.closest('#cart-icon-bubble'));
  }

  updateHeaderAccessibility() {
    const link = document.getElementById('cart-icon-bubble');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', 'CartDrawer');
    link.setAttribute('aria-expanded', String(this.classList.contains('active')));
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('active')) return;
    this.inert = false;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    this.updateHeaderAccessibility();
    this.focusDrawer();
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    this.loadDiscountCodes();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.getElementById('cart-icon-bubble'));
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    this.updateHeaderAccessibility();
  }

  focusDrawer(focusId) {
    if (!this.classList.contains('active')) return;
    const inner = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : this.querySelector('.drawer__inner');
    if (!inner) return;
    const target = focusId && inner.querySelector(`#${CSS.escape(focusId)}`);
    trapFocus(inner, (target && !target.disabled && !target.closest('[inert]') && target.getClientRects().length ? target : null) || inner.querySelector('.drawer__close') || inner);
  }

  readState(root = this) {
    try {
      return JSON.parse(root.querySelector('[data-native-cart-state]')?.textContent || 'null');
    } catch {
      return null;
    }
  }

  refreshCurrency() {
    // BUCKS' documented integration for newly rendered .money elements.
    // Shopify money filters already supply the store's configured wrappers.
    try {
      window.bucksCC?.reConvert?.();
    } catch {
      // A display-converter failure must not interrupt cart or checkout updates.
    }
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  getRefreshSections() {
    const sections = new Map(this.getSectionsToRender().map((entry) => [entry.section, entry]));
    for (const entry of document.querySelector('cart-items')?.getSectionsToRender() || []) {
      sections.set(entry.section, entry);
    }
    return [...sections.values()];
  }

  async requestCart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const cart = await response.json();
    if (!response.ok || cart.errors || cart.status) {
      throw new Error(typeof cart.description === 'string' ? cart.description : window.cartStrings.error);
    }
    return cart;
  }

  static normalizeCode(code) {
    return String(code || '').trim().toUpperCase();
  }

  static discountEntries(cart) {
    const entries = new Map();
    const add = (code, amount = 0) => {
      const key = CartDrawer.normalizeCode(code);
      if (!key) return;
      const entry = entries.get(key) || { code, amount: 0 };
      entry.amount += Number(amount) || 0;
      entries.set(key, entry);
    };
    for (const code of cart?.discount_codes || []) {
      if (code.applicable === true) add(code.code);
    }
    for (const application of cart?.cart_level_discount_applications || []) {
      if (application.type === 'discount_code') add(application.title, application.total_allocated_amount);
    }
    for (const item of cart?.items || []) {
      for (const allocation of item.line_level_discount_allocations || []) {
        if (allocation.discount_application?.type === 'discount_code') add(allocation.discount_application.title, allocation.amount);
      }
    }
    return [...entries.values()];
  }

  async readDiscountCart() {
    const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(window.cartStrings.error);
    const cart = await response.json();
    // Read the full code list before replacing it, including shipping codes
    // that Liquid doesn't expose as monetary discount allocations.
    if (!Array.isArray(cart.items) || !Array.isArray(cart.discount_codes)) throw new Error(window.cartStrings.error);
    return cart;
  }

  async loadDiscountCodes() {
    if (!this.querySelector('[data-cart-discounts]') || this.isBusy) return;
    const revision = this.discountRevision || 0;
    try {
      const cart = await this.readDiscountCart();
      if (!this.isConnected || this.isBusy || revision !== (this.discountRevision || 0)) return;
      this.updateDiscountUI(cart);
      this.refreshCurrency();
    } catch {
      // Retain server-rendered monetary codes if this optional read fails.
    }
  }

  updateDiscountUI(cart) {
    const widget = this.querySelector('[data-cart-discounts]');
    if (!widget) return;
    let amounts = [];
    try {
      amounts = JSON.parse(widget.querySelector('[data-cart-discount-data]').content.textContent);
    } catch {
      // Code application still requires a fresh, validated Shopify cart read.
    }
    const entries = cart && Array.isArray(cart.discount_codes) ? CartDrawer.discountEntries(cart) : amounts;
    const list = widget.querySelector('[data-cart-discount-codes]');
    const template = widget.querySelector('[data-cart-discount-code-template]');
    const fragment = document.createDocumentFragment();
    entries.forEach((entry, index) => {
      const row = template.content.firstElementChild.cloneNode(true);
      row.querySelector('[data-cart-discount-code-name]').textContent = entry.code;
      const benefit = row.querySelector('[data-cart-discount-benefit]');
      const money = amounts.find((amount) => CartDrawer.normalizeCode(amount.code) === CartDrawer.normalizeCode(entry.code) && amount.amount === entry.amount);
      if (entry.amount > 0 && money && typeof money.amount_html === 'string') {
        benefit.append(document.createTextNode(`${widget.dataset.savingsLabel} `));
        const value = document.createElement('span');
        // Only Shopify's money filter output is HTML; code text never is.
        value.innerHTML = money.amount_html;
        benefit.append(value);
      } else {
        benefit.textContent = entry.amount > 0 ? 'Applied' : widget.dataset.pendingLabel;
      }
      const remove = row.querySelector('[data-cart-discount-remove]');
      remove.id = `CartDrawer-DiscountRemove-${index}`;
      remove.dataset.cartDiscountRemove = entry.code;
      remove.setAttribute('aria-label', `Remove discount ${entry.code}`);
      fragment.append(row);
    });
    list.replaceChildren(fragment);
    list.hidden = !entries.length;
    const offer = widget.querySelector('[data-cart-discount-offer]');
    if (offer) offer.hidden = Boolean(entries.length);
    const offerApply = widget.querySelector('[data-cart-discount-apply]');
    if (offerApply) offerApply.hidden = Boolean(entries.length);
    widget.querySelector('[data-cart-discount-entry-label]').hidden = Boolean(entries.length || offer);
    const summary = widget.querySelector('[data-cart-discount-summary]');
    summary.replaceChildren();
    summary.hidden = !entries.length;
    if (entries.length === 1) {
      const row = list.firstElementChild;
      const name = row.querySelector('[data-cart-discount-code-name]').cloneNode(true);
      const benefit = row.querySelector('[data-cart-discount-benefit]').cloneNode(true);
      name.removeAttribute('data-cart-discount-code-name');
      benefit.removeAttribute('data-cart-discount-benefit');
      summary.append(name);
      summary.append(document.createTextNode(' · '));
      summary.append(benefit);
    } else if (entries.length > 1) {
      summary.textContent = `${entries.length} codes applied`;
    }
    widget.classList.toggle('has-offer', Boolean(offer && !entries.length));
    widget.classList.toggle('has-codes', Boolean(entries.length));
    widget.querySelector('#CartDrawer-DiscountCode').value = this.discountDraft || '';
    this.setDiscountExpanded(Boolean(this.discountDetailsOpen));
  }

  setDiscountExpanded(expanded, focusInput = false) {
    const widget = this.querySelector('[data-cart-discounts]');
    if (!widget) return;
    this.discountDetailsOpen = expanded;
    widget.querySelector('#CartDrawer-DiscountDetails').hidden = !expanded;
    widget.querySelector('[data-cart-discount-toggle]').setAttribute('aria-expanded', String(expanded));
    widget.classList.toggle('is-expanded', expanded);
    if (expanded && focusInput) widget.querySelector('#CartDrawer-DiscountCode').focus({ preventScroll: true });
  }

  showDiscountMessage(message, error = false, expand = false) {
    const output = this.querySelector('#CartDrawer-DiscountMessage');
    if (!output) return;
    output.textContent = message;
    output.hidden = !message;
    output.classList.toggle('is-error', error);
    output.classList.toggle('visually-hidden', !error);
    const input = this.querySelector('#CartDrawer-DiscountCode');
    input?.setAttribute('aria-invalid', String(error && expand));
    if (expand) {
      this.setDiscountExpanded(true);
    }
  }

  async changeDiscount(rawCode, { remove = false, featured = false } = {}) {
    const widget = this.querySelector('[data-cart-discounts]');
    if (!widget || this.isBusy || this.readState()?.merchandise_count < 1) return;
    if (this.cartNeedsRefresh) return this.recoverCart();
    const code = String(rawCode || '').trim();
    if (!code || code.length > 255 || /[,\r\n\x00-\x1f]/.test(code)) {
      this.showDiscountMessage(widget.dataset.invalidMessage, true, !featured && !remove);
      return;
    }
    this.setBusy(true);
    this.showDiscountMessage('');
    const focusId = remove ? 'CartDrawer-DiscountToggle' : featured ? 'CartDrawer-OfferApply' : 'CartDrawer-DiscountCode';
    const finish = (cart, failedRequest = false) => {
      const applied = CartDrawer.discountEntries(cart).some((entry) => CartDrawer.normalizeCode(entry.code) === CartDrawer.normalizeCode(code));
      const confirmed = remove ? !applied : applied;
      if (confirmed) {
        this.discountDraft = '';
        this.discountDetailsOpen = false;
      }
      this.updateDiscountUI(cart);
      this.refreshCurrency();
      const current = this.querySelector('[data-cart-discounts]');
      if (!current) return;
      this.showDiscountMessage(
        confirmed ? `${code} ${remove ? 'removed' : 'added to your cart'}.` : failedRequest ? current.dataset.errorMessage : current.dataset.invalidMessage,
        !confirmed, !confirmed && !featured && !remove
      );
      this.busyFocusId = confirmed ? 'CartDrawer-DiscountToggle' : focusId;
    };
    try {
      const before = await this.readDiscountCart();
      const codes = CartDrawer.discountEntries(before).map((entry) => entry.code).filter((value) => CartDrawer.normalizeCode(value) !== CartDrawer.normalizeCode(code));
      if (!remove) codes.push(code);
      let cart = await this.requestCart(routes.cart_update_url, {
        discount: codes.join(','),
        sections: this.getRefreshSections().map((entry) => entry.section),
        sections_url: window.location.pathname,
      });
      if (!Array.isArray(cart.items) || !Array.isArray(cart.discount_codes)) throw new Error(window.cartStrings.error);
      cart = await this.reconcileProtection(cart);
      if (cart.sections?.['cart-drawer']) this.renderContents(cart, { open: false, focusId });
      else await this.refreshContents({ focusId });
      finish(cart);
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cartData: cart });
    } catch {
      // A lost response can still have applied the code. Read back the cart;
      // never repeat a mutation or overwrite the other codes from stale UI.
      this.cartNeedsRefresh = true;
      try {
        const cart = await this.readDiscountCart();
        await this.refreshContents({ focusId });
        finish(cart, true);
        await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cartData: cart });
      } catch {
        this.showDiscountMessage(widget.dataset.errorMessage, true, !featured && !remove);
        this.showProtectionError(window.cartStrings.error);
      }
    } finally {
      this.setBusy(false);
    }
  }

  async reconcileProtection(cart, sectionIds = this.getRefreshSections().map((entry) => entry.section)) {
    const state = this.readState();
    if (!state?.protectionEnabled || !Array.isArray(cart?.items)) return cart;
    const protection = cart.items.filter((item) => String(item.product_id) === state.protectionProductId);
    if (!protection.length) return cart;
    const hasMerchandise = cart.items.some((item) => String(item.product_id) !== state.protectionProductId && item.quantity > 0);
    const updates = {};
    protection.forEach((item, index) => {
      const quantity = hasMerchandise && index === 0 ? 1 : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    if (!Object.keys(updates).length) return cart;
    return this.requestCart(routes.cart_update_url, {
      updates,
      sections: [...new Set(sectionIds)],
      sections_url: window.location.pathname,
    });
  }

  async normalizeInitialCart() {
    const state = this.readState();
    if (!state?.protectionEnabled) return;
    this.setBusy(true);
    try {
      const cart = await this.reconcileProtection(state);
      if (cart !== state) {
        this.renderContents(cart, { open: false });
        await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-protection', cartData: cart });
      }
    } catch {
      this.cartNeedsRefresh = true;
      this.showProtectionError(window.cartStrings.error);
    } finally {
      this.setBusy(false);
    }
  }

  async setProtection(enabled) {
    if (this.cartNeedsRefresh) return this.recoverCart();
    const state = this.readState();
    if (!state?.protectionEnabled || this.isBusy || state.merchandise_count < 1) return;
    const protection = state.items.filter((item) => String(item.product_id) === state.protectionProductId);
    if (enabled && !state.protectionAvailable && !protection.length) return;
    this.setBusy(true);
    this.showProtectionError('');
    const sectionIds = this.getRefreshSections().map((entry) => entry.section);
    const context = { sections: sectionIds, sections_url: window.location.pathname };
    try {
      let cart;
      if (enabled && !protection.length) {
        const added = await this.requestCart(routes.cart_add_url, {
          ...context,
          items: [{ id: state.protectionVariantId, quantity: 1 }],
        });
        // add.js returns the added lines, not the complete cart required by subscribers.
        const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(window.cartStrings.error);
        cart = { ...(await response.json()), sections: added.sections };
        cart = await this.reconcileProtection(cart, sectionIds);
      } else {
        const updates = Object.fromEntries(protection.map((item, index) => [item.key, enabled && index === 0 ? 1 : 0]));
        cart = await this.requestCart(routes.cart_update_url, { ...context, updates });
      }
      this.renderContents(cart, { open: false, focusId: 'CartDrawer-Protection' });
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-protection', cartData: cart });
    } catch {
      // A failed response may still have changed the server cart. Read it back;
      // never blindly retry adding a paid item after a network/rendering failure.
      this.cartNeedsRefresh = true;
      try {
        await this.refreshContents({ focusId: 'CartDrawer-Protection' });
      } catch {
        const input = this.querySelector('[data-cart-protection-input]');
        if (input) input.checked = protection.length > 0;
      }
      this.showProtectionError(window.cartStrings.error);
    } finally {
      this.setBusy(false);
    }
  }

  async recoverCart() {
    if (this.isBusy) return;
    this.setBusy(true);
    try {
      const cart = await this.refreshContents();
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-protection', cartData: cart });
      this.showProtectionError('');
    } catch {
      this.cartNeedsRefresh = true;
      this.showProtectionError(window.cartStrings.error);
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    const wasBusy = Boolean(this.isBusy);
    if (busy && !wasBusy) this.discountRevision = (this.discountRevision || 0) + 1;
    if (busy && !wasBusy) this.busyFocusId = this.contains(document.activeElement) ? document.activeElement.id : null;
    this.isBusy = busy;
    this.toggleAttribute('data-cart-busy', busy);
    this.setAttribute('aria-busy', String(busy));
    for (const items of document.querySelectorAll('cart-items, cart-drawer-items, .cart__dynamic-checkout-buttons')) {
      if (busy && !items.inert) {
        items.setAttribute('data-native-cart-inert', '');
        items.inert = true;
      } else if (!busy && items.hasAttribute('data-native-cart-inert')) {
        items.inert = false;
        items.removeAttribute('data-native-cart-inert');
      }
    }
    const input = this.querySelector('[data-cart-protection-input]');
    if (input) input.disabled = busy;
    for (const control of this.querySelectorAll('[data-cart-discounts] input, [data-cart-discounts] button')) control.disabled = busy;
    for (const checkout of document.querySelectorAll('[name="checkout"]')) {
      if ((busy || this.cartNeedsRefresh) && !checkout.disabled) {
        checkout.setAttribute('data-native-cart-disabled', '');
        checkout.disabled = true;
      } else if (!busy && !this.cartNeedsRefresh && checkout.hasAttribute('data-native-cart-disabled')) {
        checkout.disabled = false;
        checkout.removeAttribute('data-native-cart-disabled');
      }
    }
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = busy || Boolean(this.cartNeedsRefresh) || this.readState()?.merchandise_count === 0;
    if (!busy && wasBusy) this.focusDrawer(this.busyFocusId);
  }

  showProtectionError(message) {
    const error = this.querySelector('#CartDrawer-ProtectionError');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
    const retry = this.querySelector('[data-cart-retry]');
    if (retry) {
      retry.hidden = !message || !this.cartNeedsRefresh;
      retry.disabled = retry.hidden;
    }
  }

  async refreshContents({ focusId } = {}) {
    const url = new URL(routes.cart_url, window.location.origin);
    url.searchParams.set('sections', this.getRefreshSections().map((entry) => entry.section).join(','));
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(window.cartStrings.error);
    const sections = await response.json();
    if (!sections['cart-drawer']) throw new Error(window.cartStrings.error);
    const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const state = this.readState(html);
    if (!state) throw new Error(window.cartStrings.error);
    const cart = await this.reconcileProtection({ ...state, sections });
    this.renderContents(cart, { open: false, focusId });
    return cart;
  }

  renderContents(parsedState, { open = true, focusId } = {}) {
    this.discountRevision = (this.discountRevision || 0) + 1;
    const activeId = focusId || (this.contains(document.activeElement) ? document.activeElement.id : null);
    const scrollTop = this.querySelector('cart-drawer-items')?.scrollTop || 0;
    for (const section of this.getSectionsToRender()) {
      const html = parsedState.sections?.[section.section];
      if (!html) {
        if (section.section === 'cart-drawer') {
          if (open) {
            window.location.assign(routes.cart_url);
            return;
          }
          throw new Error(window.cartStrings.error);
        }
        continue;
      }
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      const target = section.id === 'cart-drawer' ? this.querySelector('#CartDrawer') : document.getElementById(section.id);
      if (!source || !target) {
        if (section.id === 'cart-drawer') throw new Error(window.cartStrings.error);
        continue;
      }
      target.innerHTML = source.innerHTML;
    }
    this.classList.toggle('is-empty', this.readState()?.merchandise_count === 0);
    this.cartNeedsRefresh = false;
    this.productId = parsedState.id;
    const items = this.querySelector('cart-drawer-items');
    if (items) items.scrollTop = scrollTop;
    this.updateDiscountUI(parsedState);
    this.setBusy(Boolean(this.isBusy));
    this.updateHeaderAccessibility();
    this.refreshCurrency();
    if (open && !this.classList.contains('active')) this.open();
    else this.focusDrawer(activeId);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
